import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import React from 'react';

// How long to wait between two version checks. Keeps the check lazy and cheap:
// we never poll per request, only on tab focus / a long fallback interval.
const MIN_CHECK_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const FALLBACK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

// Stable id so the "new version available" toast can never stack up.
const TOAST_ID = 'app-version-outdated';

// The version baked into this bundle at build time (see vite.config.js).
const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION;

// Fetches the deployed version.json. Served from the same origin as index.html,
// so this needs no backend and no auth. Cache is bypassed to read the live file.
const fetchDeployedVersion = async () => {
  const base = import.meta.env.BASE_URL || '/';
  const url = `${base}version.json?t=${Date.now()}`;
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) return null;
  const data = await response.json();
  return data?.version || null;
};

// Forces a fresh document load that bypasses the cached index.html by appending
// a cache-busting query param. A plain reload could otherwise serve the stale
// HTML (and therefore the stale hashed assets) from cache.
const forceReload = deployedVersion => {
  const params = new URLSearchParams(window.location.search);
  params.set('v', deployedVersion || Date.now().toString());
  window.location.replace(`${window.location.pathname}?${params.toString()}${window.location.hash}`);
};

// Lazily detects when a newer frontend build has been deployed and shows a
// persistent toast offering a one-click refresh. The check runs when the tab
// becomes visible (throttled) plus on a long fallback interval - never on
// individual API requests, so the runtime overhead is negligible.
const useVersionCheck = () => {
  const lastCheckRef = useRef(0);
  const notifiedRef = useRef(false);

  const showUpdateToast = useCallback(deployedVersion => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    toast.info(
      React.createElement(
        'div',
        null,
        React.createElement(
          'div',
          { style: { marginBottom: 8 } },
          'Eine neue Version ist verfügbar. Bitte aktualisieren, um auf dem neuesten Stand zu sein (alternativ Strg+Shift+R).'
        ),
        React.createElement(
          'button',
          {
            onClick: () => forceReload(deployedVersion),
            style: {
              cursor: 'pointer',
              border: 'none',
              borderRadius: 4,
              padding: '6px 12px',
              background: '#1976d2',
              color: '#fff',
              fontWeight: 600,
            },
          },
          'Jetzt aktualisieren'
        )
      ),
      {
        toastId: TOAST_ID,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      }
    );
  }, []);

  const checkVersion = useCallback(
    async ({ force = false } = {}) => {
      // No baked-in version (e.g. dev server) -> nothing to compare against.
      if (!CURRENT_VERSION) return;

      const now = Date.now();
      if (!force && now - lastCheckRef.current < MIN_CHECK_INTERVAL_MS) return;
      lastCheckRef.current = now;

      try {
        const deployedVersion = await fetchDeployedVersion();
        if (deployedVersion && deployedVersion !== CURRENT_VERSION) {
          showUpdateToast(deployedVersion);
        }
      } catch {
        // Network hiccup or missing version.json (e.g. dev) - ignore silently.
      }
    },
    [showUpdateToast]
  );

  useEffect(() => {
    if (!CURRENT_VERSION) return undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const intervalId = setInterval(() => checkVersion(), FALLBACK_INTERVAL_MS);

    // One initial check shortly after mount.
    checkVersion({ force: true });

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      clearInterval(intervalId);
    };
  }, [checkVersion]);
};

export default useVersionCheck;
